// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Lakko Contributors

/**
 * Three numbered word fields: the check step of the signup phrase screen
 * and the local three-word sign-in.
 *
 * Each field is labelled with the position asked for, in the order given
 * (say 7, 3 and 12; the positions are drawn on the device), suggests words from the BIP39 English list (`WordSuggest`, the app's own list), refuses a word that is
 * not in it (red outline, and the caller's Continue stays disabled), and a
 * paste of the whole 12-word phrase into any field fills the three positions
 * from it, unless `allowPhrasePaste` is false (the signup check, where the
 * words must come from the user's notes). The value is the three words in
 * field order.
 */
import { Index, type JSX } from "solid-js";
import { isBip39Word } from "../recovery/bip39-words";
import { RECOVERY_PHRASE_WORDS } from "../recovery/phrase-shape";
import { t } from "../l10n";
import { createWordSuggest } from "./WordSuggest";

export function WordSlots(props: {
  positions: () => readonly number[];
  value: () => readonly string[];
  setValue: (v: string[]) => void;
  disabled?: () => boolean;
  /** Fill the slots from a pasted whole phrase (default true). */
  allowPhrasePaste?: boolean;
}): JSX.Element {
  const inputs: HTMLInputElement[] = [];
  const words = (): string[] => {
    const w = [...props.value()];
    while (w.length < props.positions().length) w.push("");
    return w.slice(0, props.positions().length);
  };
  const setWord = (i: number, raw: string): void => {
    const next = words();
    next[i] = raw.toLowerCase().replace(/[^a-z]/g, "");
    props.setValue(next);
  };
  const focusSlot = (i: number): void => {
    const el = inputs[Math.max(0, Math.min(inputs.length - 1, i))];
    el?.focus();
    el?.select();
  };
  const onPaste = (e: ClipboardEvent): void => {
    if (props.allowPhrasePaste === false) return;
    const parts = (e.clipboardData?.getData("text") ?? "")
      .toLowerCase()
      .split(/[^a-z]+/)
      .filter((p) => p.length > 0);
    if (parts.length !== RECOVERY_PHRASE_WORDS) return;
    e.preventDefault();
    props.setValue(props.positions().map((p) => parts[p - 1] ?? ""));
  };
  const suggest = createWordSuggest({
    word: (i) => words()[i] ?? "",
    accept: (i, word) => {
      setWord(i, word);
      if (i < inputs.length - 1) focusSlot(i + 1);
    },
  });
  const onKeyDown = (i: number, e: KeyboardEvent & { currentTarget: HTMLInputElement }): void => {
    if (suggest.onKeyDown(i, e)) return;
    if ((e.key === " " || e.key === "Enter") && i < inputs.length - 1 && e.currentTarget.value.length > 0) {
      e.preventDefault();
      focusSlot(i + 1);
    } else if (e.key === " ") {
      e.preventDefault();
    } else if (e.key === "Backspace" && e.currentTarget.value.length === 0 && i > 0) {
      e.preventDefault();
      focusSlot(i - 1);
    }
  };

  return (
    <div class="lk-word-slots">
      <Index each={props.positions()}>
        {(pos, i) => (
          <label class="lk-word-slot">
            <span class="lk-word-slot-num">{pos()}</span>
            <input
              ref={(el) => {
                inputs[i] = el;
              }}
              type="text"
              role="combobox"
              aria-autocomplete="list"
              aria-expanded={suggest.expanded(i)}
              aria-controls={suggest.controls(i)}
              aria-activedescendant={suggest.activeDescendant(i)}
              value={words()[i] ?? ""}
              classList={{
                invalid: (words()[i] ?? "").length > 0 && !isBip39Word(words()[i] ?? ""),
              }}
              autocomplete="off"
              autocapitalize="none"
              autocorrect="off"
              spellcheck={false}
              disabled={props.disabled?.() === true}
              aria-label={t("recovery_phrase.word_label", { position: String(pos()) })}
              placeholder={t("recovery_phrase.word_placeholder", { position: String(pos()) })}
              onInput={(e) => {
                setWord(i, e.currentTarget.value);
                suggest.onInput();
              }}
              onFocus={() => suggest.onFocus(i)}
              onBlur={() => suggest.onBlur(i)}
              onPaste={onPaste}
              onKeyDown={(e) => onKeyDown(i, e)}
            />
            <suggest.List slot={i} />
          </label>
        )}
      </Index>
    </div>
  );
}
